const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;
const DAY_MS = 86_400_000;
// geometric horizon plus refraction and the sun's radius
const SUNRISE_ZENITH = 90.833;

export interface SunPosition {
  // degrees above the horizon
  altitude: number;
  // degrees clockwise from north
  azimuth: number;
}

export interface SunTimes {
  sunrise: Date | null;
  noon: Date;
  sunset: Date | null;
}

interface Orbit {
  declination: number;
  // equation of time, minutes
  eqTime: number;
}

// NOAA solar calculator (Meeus, low accuracy): declination and equation of time for an instant.
function orbit(date: Date): Orbit {
  const jc = (date.getTime() / DAY_MS + 2440587.5 - 2451545) / 36525;
  const l0 = (280.46646 + jc * (36000.76983 + jc * 0.0003032)) % 360;
  const m = 357.52911 + jc * (35999.05029 - 0.0001537 * jc);
  const e = 0.016708634 - jc * (0.000042037 + 0.0000001267 * jc);
  const c =
    Math.sin(m * RAD) * (1.914602 - jc * (0.004817 + 0.000014 * jc)) +
    Math.sin(2 * m * RAD) * (0.019993 - 0.000101 * jc) +
    Math.sin(3 * m * RAD) * 0.000289;
  const omega = 125.04 - 1934.136 * jc;
  const lambda = l0 + c - 0.00569 - 0.00478 * Math.sin(omega * RAD);
  const eps0 = 23 + (26 + (21.448 - jc * (46.815 + jc * (0.00059 - jc * 0.001813))) / 60) / 60;
  const eps = eps0 + 0.00256 * Math.cos(omega * RAD);
  const declination = Math.asin(Math.sin(eps * RAD) * Math.sin(lambda * RAD)) * DEG;
  const y = Math.tan((eps / 2) * RAD) ** 2;
  const eqTime =
    4 *
    DEG *
    (y * Math.sin(2 * l0 * RAD) -
      2 * e * Math.sin(m * RAD) +
      4 * e * y * Math.sin(m * RAD) * Math.cos(2 * l0 * RAD) -
      0.5 * y * y * Math.sin(4 * l0 * RAD) -
      1.25 * e * e * Math.sin(2 * m * RAD));
  return { declination, eqTime };
}

export function sunPosition(lat: number, lon: number, date: Date): SunPosition {
  const { declination, eqTime } = orbit(date);
  const minutes = (date.getTime() % DAY_MS) / 60_000;
  const trueSolar = (((minutes + eqTime + 4 * lon) % 1440) + 1440) % 1440;
  const hourAngle = trueSolar / 4 - 180;
  const phi = lat * RAD;
  const delta = declination * RAD;
  const cosZenith =
    Math.sin(phi) * Math.sin(delta) + Math.cos(phi) * Math.cos(delta) * Math.cos(hourAngle * RAD);
  const zenith = Math.acos(Math.min(1, Math.max(-1, cosZenith)));
  const cosAz =
    (Math.sin(phi) * Math.cos(zenith) - Math.sin(delta)) / (Math.cos(phi) * Math.sin(zenith));
  const az = Math.acos(Math.min(1, Math.max(-1, cosAz))) * DEG;
  const azimuth = hourAngle > 0 ? (az + 180) % 360 : (540 - az) % 360;
  return { altitude: 90 - zenith * DEG, azimuth };
}

// Sunrise, solar noon and sunset of the UTC day that contains `day`; null in polar day or night.
export function sunTimes(lat: number, lon: number, day: Date): SunTimes {
  const midnight = Math.floor(day.getTime() / DAY_MS) * DAY_MS;
  const { declination, eqTime } = orbit(new Date(midnight + DAY_MS / 2));
  const phi = lat * RAD;
  const delta = declination * RAD;
  const noonMin = 720 - 4 * lon - eqTime;
  const cosH =
    Math.cos(SUNRISE_ZENITH * RAD) / (Math.cos(phi) * Math.cos(delta)) -
    Math.tan(phi) * Math.tan(delta);
  const at = (min: number) => new Date(midnight + min * 60_000);
  if (cosH < -1 || cosH > 1) return { sunrise: null, noon: at(noonMin), sunset: null };
  const half = 4 * Math.acos(cosH) * DEG;
  return { sunrise: at(noonMin - half), noon: at(noonMin), sunset: at(noonMin + half) };
}

// Unit vector towards the sun: x east, y up, z south (north is -z, as in the scene).
export function sunDirection({ altitude, azimuth }: SunPosition): [number, number, number] {
  const alt = altitude * RAD;
  const az = azimuth * RAD;
  return [Math.sin(az) * Math.cos(alt), Math.sin(alt), -Math.cos(az) * Math.cos(alt)];
}
